import { ProductsService, default_filters } from '@/modules/products/services';
import { FiltersPagination } from '@/types';
import { Actions } from 'vuex-smart-module';
import {
  CatalogRequest,
  ProdQtyUpdateDTO,
  Products,
  ProductsFilters,
  ProductsList,
} from '../types';
import ProductGetters from './getters';
import ProductMutations from './mutations';
import ProductState from './state';

const service = new ProductsService();

export default class ProductActions extends Actions<
  ProductState,
  ProductGetters,
  ProductMutations,
  // eslint-disable-next-line no-use-before-define
  ProductActions
> {
  async get_products(storage_limit?: number) {
    this.mutations.SET_LOADING(true);

    try {
      const { sortBy = [], sortDesc = [] } = this.state.table_options;
      const { current_page, limit } = this.state.pagination_props;
      const payload: FiltersPagination<ProductsFilters> = {
        filters: this.state.filters,
        pagination: {
          limit: storage_limit || limit,
          page: current_page,
          sort: '',
          sort_type: 'asc',
        },
      };

      if (sortBy?.length) {
        [payload.pagination.sort] = sortBy;
      }

      if (sortDesc?.length) {
        payload.pagination.sort_type = sortDesc[0] ? 'desc' : 'asc';
      }

      const { data, meta, code } = await service.get_products(payload);

      const add_qty = data.items.map((e) => {
        if (e.related_products && e.related_products.length) {
          e.related_products.forEach((r) => ({
            ...r,
            quantity: e?.quantity || 0,
            parent_id: e.id,
          }));
        }
        return {
          ...e,
          quantity: e?.quantity || 0,
          selected: false,
        };
      });

      this.mutations.SET_PRODUCTS(add_qty);
      this.mutations.SET_CATEGORIES(data.categories);
      this.mutations.SET_IS_NEW_CATALOG_TOGGLE(data.is_new_catalog);
      this.mutations.SET_SOFTWARE_TYPES(data.software_types);
      this.mutations.UPD_TABLE_OPTIONS({ key: 'itemsPerPage', value: meta.limit });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'current_page', value: meta.current_page });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'total_pages', value: meta.total_pages });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'total_items', value: meta.total_items });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'limit', value: meta.limit });

      return code;
    } catch (error) {
      console.log(error);
    } finally {
      this.mutations.SET_LOADING(false);
    }
  }

  /**
   * Временная копия функции get_products для приведения к единообразию пагинации
   */
  async get_products2(storage_limit?: number) {
    this.mutations.SET_LOADING(true);

    try {
      const {
        sortBy = [], sortDesc = [], page = 1, itemsPerPage = 10,
      } = this.state.table_options;
      const payload: FiltersPagination<ProductsFilters> = {
        filters: this.state.filters,
        pagination: {
          limit: storage_limit ?? itemsPerPage,
          page,
          sort: '',
          sort_type: 'asc',
        },
      };

      if (sortBy?.length) {
        [payload.pagination.sort] = sortBy;
      }

      if (sortDesc?.length) {
        payload.pagination.sort_type = sortDesc[0] ? 'desc' : 'asc';
      }

      const { data, meta } = await service.get_products(payload);

      const add_qty = data.items.map((e) => {
        if (e.related_products && e.related_products.length) {
          e.related_products.forEach((r) => ({
            ...r,
            quantity: e?.quantity || 0,
            parent_id: e.id,
          }));
        }
        return {
          ...e,
          quantity: e?.quantity || 0,
          selected: false,
        };
      });

      this.mutations.SET_PRODUCTS(add_qty);
      this.mutations.SET_CATEGORIES(data.categories);
      this.mutations.SET_IS_NEW_CATALOG_TOGGLE(data.is_new_catalog);
      this.mutations.SET_SOFTWARE_TYPES(data.software_types);
      this.mutations.UPD_TABLE_OPTIONS({ key: 'itemsPerPage', value: meta.limit });
      this.mutations.UPD_TABLE_OPTIONS({ key: 'page', value: meta.current_page });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'current_page', value: meta.current_page });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'total_pages', value: meta.total_pages });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'total_items', value: meta.total_items });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'limit', value: meta.limit });
    } catch (error) {
      console.log(error);
    } finally {
      this.mutations.SET_LOADING(false);
    }
  }

  async get_catalog(storage_limit?: number) {
    this.mutations.SET_LOADING(true);

    try {
      const {
        sortBy = [], sortDesc = [], page = 1, itemsPerPage = 10,
      } = this.state.table_options;

      const filtersEmpty = this.state.catalog_categories.length < 1;

      const payload: CatalogRequest = {
        filters: this.state.catalog_filters,
        filters_exist: !filtersEmpty,
        pagination: {
          limit: storage_limit ?? itemsPerPage,
          page,
          sort: '',
          sort_type: 'asc',
        },
      };

      if (sortBy?.length) {
        [payload.pagination.sort] = sortBy;
      }

      if (sortDesc?.length) {
        payload.pagination.sort_type = sortDesc[0] ? 'desc' : 'asc';
      }

      const { data, meta } = await service.get_catalog(payload);

      const items = data.items.map((e) => {
        if (e.related_products && e.related_products.length) {
          e.related_products.forEach((r) => ({
            ...r,
            quantity: e?.quantity || 0,
            parent_id: e.id,
          }));
        }

        return e.is_required_license ? {
          ...e,
          license_keys: [],
          quantity: e?.quantity || 0,
          selected: false,
        } : {
          ...e,
          quantity: e?.quantity || 0,
          selected: false,
        };
      });

      this.mutations.SET_CATALOG(items);

      if (filtersEmpty) {
        this.mutations.SET_CATALOG_CATEGORIES(data.filters.categories);
        this.mutations.SET_CATALOG_SOFTWARE_TYPES(data.filters.software_types);
        this.mutations.SET_CATALOG_EQUIPMENT_MODELS(data.filters.equipment_models);
      }

      this.mutations.SET_HAS_REALIZATION_CONTRACT(data.filters.has_realization_contract);

      this.mutations.UPD_TABLE_OPTIONS({ key: 'itemsPerPage', value: meta.limit });
      this.mutations.UPD_TABLE_OPTIONS({ key: 'page', value: meta.current_page });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'current_page', value: meta.current_page });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'total_pages', value: meta.total_pages });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'total_items', value: meta.total_items });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'limit', value: meta.limit });
    } catch (error) {
      console.log(error);
    } finally {
      this.mutations.SET_LOADING(false);
    }
  }

  upd_product_qty(item: ProdQtyUpdateDTO | ProdQtyUpdateDTO[]) {
    this.mutations.UPD_PRODUCT_QTY(item);
  }

  upd_product_qty1(item: ProdQtyUpdateDTO | ProdQtyUpdateDTO[]) {
    this.mutations.UPD_CATALOG_PRODUCT_QTY(item);
  }

  async get_product_by_id(id: number) {
    try {
      const { data, code } = await service.get_product_by_id(id);

      this.mutations.SET_CURRENT_PRODUCT(data);
      return code;
    } catch (error) {
      console.log(error);
    }
  }

  upd_current_product(item: Products.Product) {
    this.mutations.UPD_CURRENT_PRODUCT(item);
  }

  async get_characteristics(id: string) {
    try {
      const { data } = await service.get_prod_characteristics(id);

      this.mutations.SET_CHARS_PROD(data);
    } catch (error) {
      console.log(error);
    }
  }

  async get_equipment_model() {
    try {
      const categories = this.state.zip_cat_filter;
      const params = { filters: { categories } };
      const { data } = await service.get_equipment_model(params);

      this.mutations.SET_MODELS(data.items);

      return data;
    } catch (error) {
      console.log(error);
    }
  }

  async search_products(search: string) {
    try {
      const data = await service.search_products(search);

      return data as ProductsList;
    } catch (error) {
      console.log(error);
    }
  }

  async get_prices_types() {
    try {
      const { data } = await service.get_prices_types();

      this.mutations.SET_PRICES_TYPES(data);
    } catch (error) {
      console.log(error);
    }
  }

  reset_table() {
    this.mutations.SET_FILTERS(default_filters());
    this.mutations.RESET_TABLE_OPTIONS();
  }

  // запрос ключа когда выбран один товар
  async get_license_keys(id: number) {
    try {
      if (id) {
        const { data } = await service.get_license_keys(id, this.state.search_key);

        this.mutations.SET_CURRENT_LICENSE_KEYS(data.items);
      }
    } catch (error) {
      console.error(error);
    }
  }

  // запрос ключа когда выбрано несколько товаров
  async get_license_keys_for_prd(id: number) {
    try {
      if (id) {
        const { data } = await service.get_license_keys(id, this.state.search_key);

        return data;
      }
    } catch (error) {
      console.error(error);
    }
  }

  async add_to_favorites(id: number) {
    try {
      const data = await service.add_to_favorites(id);

      return data.code;
    } catch (error) {
      console.error(error);
    }
  }

  async remove_from_favorites(id: number) {
    try {
      const data = await service.remove_from_favorites(id);

      return data.code;
    } catch (error) {
      console.error(error);
    }
  }
}
